import { useRef } from "react";
import { useFormContext, useFormState, useWatch } from "react-hook-form";
import Toggle from "@/app/components/ui/Toggles";
import {
  cardClassName,
  IHomeTileConfigFormFields,
  IPersonaCombinationSummary,
  RIBBON_TEXT_MAX_LENGTH,
  RibbonTextSuggestion,
  getRibbonTextLength,
  fieldLabelClassName,
  textInputClassName,
} from "../interface";
import { RibbonTextSuggestions } from "../controls";
import { PersonaLogicField } from "./PersonaLogicField";
import { hasStaticRibbon, validateRibbonTextValue } from "./formUtils";

const RibbonTextField = () => {
  const { control, getValues, register, setValue } = useFormContext<IHomeTileConfigFormFields>();
  const value = useWatch({ control, name: "ribbon_text" }) ?? "";
  const ribbon = useWatch({ control, name: "ribbon" });
  const { errors } = useFormState({ control, name: "ribbon_text" });
  const inputRef = useRef<HTMLInputElement | null>(null);
  const length = getRibbonTextLength(value);

  const { ref, ...registration } = register("ribbon_text", {
    validate: (next) => !hasStaticRibbon(getValues()) || validateRibbonTextValue(next),
  });

  const insertEmoji = (emoji: string) => {
    const currentValue = getValues("ribbon_text") ?? "";
    const input = inputRef.current;
    const start = input?.selectionStart ?? currentValue.length;
    const end = input?.selectionEnd ?? currentValue.length;
    setValue("ribbon_text", `${currentValue.slice(0, start)}${emoji}${currentValue.slice(end)}`, {
      shouldValidate: true,
      shouldDirty: true,
    });
    requestAnimationFrame(() => {
      const caret = start + emoji.length;
      input?.focus();
      input?.setSelectionRange(caret, caret);
    });
  };

  const applySuggestion = ({ text, bg_color, text_color, border_color }: RibbonTextSuggestion) => {
    const currentText = getValues("ribbon_text") ?? "";
    const nextText = currentText.trim() ? currentText : text;
    if (nextText !== currentText) setValue("ribbon_text", nextText, { shouldValidate: true, shouldDirty: true });
    setValue("ribbon.bg_color", bg_color, { shouldDirty: true });
    setValue("ribbon.text_color", text_color, { shouldDirty: true });
    setValue("ribbon.border_color", border_color, { shouldDirty: true });
    requestAnimationFrame(() => {
      inputRef.current?.focus();
      inputRef.current?.setSelectionRange(nextText.length, nextText.length);
    });
  };

  return (
    <div>
      <label className={fieldLabelClassName} htmlFor="ribbon-text">
        Ribbon Text
      </label>
      <input
        {...registration}
        ref={(element) => {
          ref(element);
          inputRef.current = element;
        }}
        id="ribbon-text"
        type="text"
        placeholder="e.g. Flat 20% OFF"
        className={`${textInputClassName} ${errors.ribbon_text ? "border-rose-400" : ""}`}
      />
      <div className="mt-1.5 flex items-start justify-between gap-2">
        <p className="text-[11px] text-rose-600">{errors.ribbon_text?.message ?? ""}</p>
        <span
          className={`shrink-0 text-[11px] tabular-nums ${
            length > RIBBON_TEXT_MAX_LENGTH ? "font-semibold text-rose-600" : "text-slate-400"
          }`}
        >
          {length}/{RIBBON_TEXT_MAX_LENGTH}
        </span>
      </div>
      <RibbonTextSuggestions
        disabled={length >= RIBBON_TEXT_MAX_LENGTH}
        onSelect={applySuggestion}
        onInsertEmoji={insertEmoji}
        selectedStyle={ribbon}
      />
    </div>
  );
};

export const RibbonRuleCard = ({
  personas,
  isPersonaListLoading,
  validatePersona,
  existingConfigs,
  isServiceTile,
}: {
  personas: string[];
  isPersonaListLoading: boolean;
  validatePersona: (value: string) => true | string;
  existingConfigs: IPersonaCombinationSummary[];
  isServiceTile: boolean;
}) => {
  const { control, setValue } = useFormContext<IHomeTileConfigFormFields>();
  const status = useWatch({ control, name: "status" });
  const overridesDynamic = useWatch({ control, name: "overrides_dynamic" });

  const setIsActive = (nextIsActive: boolean) => {
    setValue("status", nextIsActive ? "ACTIVE" : "INACTIVE", { shouldDirty: true });
  };

  return (
    <div className={cardClassName}>
      <div className="flex flex-wrap items-center gap-3 border-b border-slate-100 px-5 py-4">
        <span className="text-sm font-semibold text-gray-900">Ribbon</span>
        <div className="ml-auto flex flex-wrap items-center gap-5">
          {isServiceTile && (
            <div className="flex items-center gap-2.5">
              <span className="text-xs font-medium text-slate-600">Overwrite dynamic rule</span>
              <Toggle
                key={`rule-overrides-dynamic-${overridesDynamic}`}
                checked={overridesDynamic}
                onToggle={(next: boolean) => setValue("overrides_dynamic", next, { shouldDirty: true })}
              />
            </div>
          )}
          <div className="flex items-center gap-2.5">
            <span className="text-xs font-medium text-slate-600">Status</span>
            <Toggle key={`rule-status-${status}`} checked={status === "ACTIVE"} onToggle={setIsActive} />
          </div>
        </div>
      </div>

      <div className="px-5 py-4">
        <PersonaLogicField
          personas={personas}
          isPersonaListLoading={isPersonaListLoading}
          validate={validatePersona}
          existingConfigs={existingConfigs}
        />
        <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
          <RibbonTextField />
        </div>
      </div>
    </div>
  );
};
